import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';

interface DatePickerFieldProps extends Pick<React.AriaAttributes, 'aria-invalid' | 'aria-describedby'> {
  label?: string;
  /** ISO date string 'YYYY-MM-DD' or '' when empty. */
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  id?: string;
  placeholder?: string;
  min?: string;
  clearLabel?: string;
}

// Parse 'YYYY-MM-DD' into a local Date (no timezone shift).
const parseISO = (s: string): Date | undefined => {
  if (!s) return undefined;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
};

// Format a Date back to 'YYYY-MM-DD'.
const toISO = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const GAP = 8; // px gap between the field and the popover
const MARGIN = 8; // px min distance from the viewport edges

const DatePickerField: React.FC<DatePickerFieldProps> = ({
  label,
  value,
  onChange,
  required,
  id,
  placeholder = 'Select a date',
  min,
  clearLabel,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const fallbackId = React.useId();
  const fieldId = id || `date-${fallbackId}`;
  const selected = parseISO(value);
  const minimum = parseISO(min || '');
  const validationAttributes: Pick<React.AriaAttributes, 'aria-invalid' | 'aria-describedby'> = {
    'aria-invalid': ariaInvalid,
    'aria-describedby': ariaDescribedBy,
  };

  // Position the (portaled, fixed) popover relative to the trigger, flipping
  // up/left so it never spills past the viewport. Rendered in a portal on
  // document.body so an ancestor's `overflow-hidden` (e.g. Card) can't clip it.
  const reposition = useCallback(() => {
    const trigger = triggerRef.current;
    const popover = popoverRef.current;
    if (!trigger || !popover) return;
    const t = trigger.getBoundingClientRect();
    const pw = popover.offsetWidth;
    const ph = popover.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = t.left;
    if (left + pw > vw - MARGIN) left = Math.max(MARGIN, t.right - pw);
    if (left < MARGIN) left = MARGIN;

    let top = t.bottom + GAP;
    if (top + ph > vh - MARGIN) {
      const above = t.top - GAP - ph;
      top = above >= MARGIN ? above : Math.max(MARGIN, vh - MARGIN - ph);
    }
    setCoords({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition, value]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onReflow = () => reposition();
    document.addEventListener('mousedown', onDocMouseDown);
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [open, reposition]);

  const display = selected
    ? selected.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={fieldId} className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
          {label}{required && <span className="text-red-500"> *</span>}
        </label>
      )}
      <div className="flex items-stretch gap-2">
        <button
          ref={triggerRef}
          type="button"
          id={fieldId}
          {...validationAttributes}
          onClick={() => setOpen((o) => !o)}
          className="flex min-h-11 min-w-0 flex-1 items-center justify-between px-3 py-2 border rounded-md shadow-sm text-left transition duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
        >
          <span className={display ? '' : 'text-gray-500 dark:text-gray-400'}>{display || placeholder}</span>
          <svg className="w-5 h-5 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </button>
        {value && clearLabel && (
          <button
            type="button"
            aria-label={clearLabel}
            onClick={() => onChange('')}
            className="flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-600 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:text-gray-100"
          >
            <svg aria-hidden="true" className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 6l12 12M18 6L6 18" />
            </svg>
            <span>{clearLabel}</span>
          </button>
        )}
      </div>

      {open && createPortal(
        <div
          ref={popoverRef}
          style={{ position: 'fixed', top: coords.top, left: coords.left }}
          className="z-50 rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-900"
        >
          <DayPicker
            mode="single"
            selected={selected}
            disabled={minimum ? { before: minimum } : undefined}
            defaultMonth={selected}
            onSelect={(date) => {
              if (date) onChange(toISO(date));
              setOpen(false);
            }}
            showOutsideDays
            captionLayout="dropdown"
            startMonth={new Date(2000, 0)}
            endMonth={new Date(new Date().getFullYear() + 2, 11)}
          />
        </div>,
        document.body,
      )}
    </div>
  );
};

export default DatePickerField;
